import { useState } from 'react';

import styled from 'styled-components';

import { Button } from '@components/button';
import { Card } from '@components/card';
import { CodeBlock } from '@components/code-block';
import { FormLabel } from '@components/form-label';
import { HideContainer } from '@components/hide-container';
import { IconAiAgent } from '@components/icons/ai-agent';
import { IconFieldCustom } from '@components/icons/field-custom';
import { IconOutput } from '@components/icons/output';
import { CloseButton, ContentSeparator, SimpleIconHeader } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { TextArea } from '@components/textarea';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { asteriskLabel } from '@utils/strings';

import { unwrapRoot } from './list-utils';

import {
  useJqValidation,
  useValidateListTransformJq,
  useGenerateListTransformJq,
} from '../integrations-hooks';

export type EditSyncListTransformPanelProps = {
  exampleData: object | null;
  exampleDataWrapped: boolean;
  initialJq: string | null;
  onSave: (jq: string) => void;
  onClose: () => void;
};

export const EditSyncListTransformPanel = ({
  exampleData,
  exampleDataWrapped,
  initialJq,
  onSave,
  onClose,
}: EditSyncListTransformPanelProps) => {
  const [aiPrompt, setAiPrompt] = useState('');
  const [jqQuery, setJqQuery] = useState(initialJq ?? '');
  const { t } = useScopedTranslate(locale);

  const displayData = exampleData ? unwrapRoot(exampleData, exampleDataWrapped) : null;

  const validateMutation = useValidateListTransformJq();
  const generateMutation = useGenerateListTransformJq();
  const { isValidationPending } = useJqValidation(validateMutation, jqQuery, displayData);

  const handleGenerateQuery = () => {
    if (!aiPrompt || !displayData) {
      return;
    }
    generateMutation.mutate(
      { prompt: aiPrompt, exampleData: displayData },
      {
        onSuccess: (result) => {
          if (result.success && result.query) {
            setJqQuery(result.query);
          }
        },
      }
    );
  };

  const handleSave = () => {
    onSave(jqQuery);
    onClose();
  };

  const hasValidationError = Boolean(validateMutation.data?.error);
  const isDirty = jqQuery !== (initialJq ?? '');
  const queryResult = validateMutation.data?.result;
  // The transform has to produce a list/object of options — a scalar or no
  // output would fail at sync time.
  const isResultValidShape = typeof queryResult === 'object' && queryResult !== null;
  const isSaveDisabled =
    !jqQuery ||
    !isDirty ||
    isValidationPending ||
    hasValidationError ||
    !isResultValidShape ||
    generateMutation.isLoading;

  const isValidationComplete = Boolean(jqQuery) && !isValidationPending;
  const validationSucceeded = isValidationComplete && Boolean(validateMutation.data?.success);
  const showEmptyState = !jqQuery;
  const showValidating = Boolean(jqQuery) && isValidationPending;
  const showRequestError = isValidationComplete && validateMutation.isError;
  const showQueryError = isValidationComplete && hasValidationError;
  const showEmptyResult = validationSucceeded && queryResult === null;
  const showInvalidShape = validationSucceeded && queryResult !== null && !isResultValidShape;
  const showResult = validationSucceeded && isResultValidShape;

  return (
    <PanelLayout>
      <StickyHeader>
        <Stack space={4}>
          <Stack space={2} orientation="horizontal" align="center" justify="space-between">
            <SimpleIconHeader
              icon={IconFieldCustom}
              iconColor="elementBlue100"
              title={t(initialJq ? 'editCustomOptions' : 'addCustomOptions')}
            />
            <CloseButton onClose={onClose} />
          </Stack>
          <ContentSeparator />
        </Stack>
      </StickyHeader>
      <ScrollContent>
        <Stack space={6}>
          <HideContainer title={t('endpointData')}>
            {displayData ? (
              <CodeBlock data={displayData} hideFooter />
            ) : (
              <Stack space={1}>
                <Text text={t('noEndpointData')} font="semibold14" color="orange100" />
                <Text text={t('testEndpointFirst')} font="regular14" color="textSecondary" />
              </Stack>
            )}
          </HideContainer>

          <ContentSeparator />

          <HideContainer title={t('configuration')}>
            <Stack space={4}>
              {displayData && (
                <Stack space={2}>
                  <Text text={t('aiPrompt')} font="semibold14" aiGradient />
                  <TextArea
                    rows={2}
                    value={aiPrompt}
                    onChange={setAiPrompt}
                    placeholder={t('aiPromptPlaceholder')}
                    isDisabled={validateMutation.isLoading || generateMutation.isLoading}
                  />
                  <div>
                    <Button
                      text={generateMutation.isLoading ? t('generating') : t('generateJqQuery')}
                      variant="ai-gradient"
                      iconLeft={<IconAiAgent variant="gradient" />}
                      onClick={handleGenerateQuery}
                      isDisabled={
                        !aiPrompt || generateMutation.isLoading || validateMutation.isLoading
                      }
                    />
                  </div>
                  {!generateMutation.isLoading && generateMutation.data?.error && (
                    <Text text={generateMutation.data.error} font="regular14" color="red100" />
                  )}
                </Stack>
              )}

              <Stack space={1}>
                <FormLabel text={asteriskLabel(t('core.jqQuery'))} />
                <TextArea
                  rows={3}
                  value={jqQuery}
                  onChange={setJqQuery}
                  isDisabled={generateMutation.isLoading}
                />
                <Text text={t('queryInfo')} font="regular14" color="textSecondary" />
              </Stack>
            </Stack>
          </HideContainer>

          <ContentSeparator />

          <HideContainer title={t('result')}>
            {showEmptyState && (
              <Card space={6} variant="gray">
                <Stack space={4} orientation="horizontal" align="center">
                  <IconOutput color="textSecondary" />
                  <Text text={t('addQueryForOutput')} font="semibold14" color="textSecondary" />
                </Stack>
              </Card>
            )}

            <div role="status" aria-live="polite">
              {showValidating && (
                <Text text={t('validating')} font="regular14" color="textSecondary" />
              )}
              {showRequestError && (
                <Text text={t('validationFailed')} font="regular14" color="red100" />
              )}
              {showQueryError && (
                <Text text={validateMutation.data?.error ?? ''} font="regular14" color="red100" />
              )}
              {showEmptyResult && <Text text={t('resultEmpty')} font="regular14" color="red100" />}
              {showInvalidShape && (
                <Text text={t('resultMustBeList')} font="regular14" color="red100" />
              )}
              {showResult && <CodeBlock data={queryResult} hideFooter />}
            </div>
          </HideContainer>
        </Stack>
      </ScrollContent>
      <PanelFooter>
        <Stack orientation="horizontal" space={2} justify="flex-end">
          <Button text={t('core.cancel')} variant="secondary" onClick={onClose} />
          <Button
            text={t('core.save')}
            variant="primary-blue"
            onClick={handleSave}
            isDisabled={isSaveDisabled}
          />
        </Stack>
      </PanelFooter>
    </PanelLayout>
  );
};

// Padding comes from the extended-sidebar container, not here, so the header
// title lines up with the neighbouring sidebar header — see FieldMappingSidebar.
const PanelLayout = styled.div({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
});

const StickyHeader = styled.div({
  position: 'sticky',
  top: 0,
  zIndex: 1,
  backgroundColor: theme.color.backgroundLightDark,
});

const ScrollContent = styled.div({
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  overflowX: 'hidden',
  paddingTop: pxToRem(24),
  paddingBottom: pxToRem(40),
});

// Bleed to the container edges so the top border spans full width.
const PanelFooter = styled.div({
  margin: `0 ${pxToRem(-24)}`,
  padding: `${pxToRem(16)} ${pxToRem(24)} 0`,
  borderTop: `${pxToRem(1)} solid ${theme.color.border10}`,
  backgroundColor: theme.color.backgroundLightDark,
});

const locale = {
  en: {
    addCustomOptions: 'Add custom options',
    editCustomOptions: 'Edit custom options',
    endpointData: 'Endpoint data',
    configuration: 'Configuration',
    result: 'Result',
    noEndpointData: 'No endpoint data available',
    testEndpointFirst:
      'Test the endpoint first to generate example data. The jq query will run against the test response.',
    queryInfo:
      'The jq query will be executed against the API response when the integration runs. Refer to https://jqlang.github.io/jq/ for syntax.',
    addQueryForOutput: 'Add or generate jq query to get output',
    validating: 'Validating query...',
    validationFailed: 'Could not validate the query. Please try again.',
    resultEmpty: 'The jq query returned no data.',
    resultMustBeList: 'The query must return a list of options, not a single value.',
    aiPrompt: 'AI Prompt',
    aiPromptPlaceholder: 'Describe what you want to select and have AI structure it for you',
    generateJqQuery: 'Generate jq query',
    generating: 'Generating...',
  },
};
